import { supabase } from './supabaseClient';

export interface Channel {
  id: string;
  user_id: string;
  channel_name: string;
  handle: string;
  bio: string;
  logo_url: string;
  banner_url: string;
  is_verified: boolean;
  monetization_status: 'none' | 'pending' | 'approved' | 'rejected';
  total_subscribers: number;
  total_views: number;
  created_at: string;
}

export interface Album {
  id: string;
  channel_id: string;
  title: string;
  description: string;
  cover_image_url: string;
  genre: string;
  language: string;
  is_published: boolean;
  total_tracks: number;
  created_at: string;
}

export interface Episode {
  id: string;
  channel_id: string;
  album_id?: string;
  title: string;
  description: string;
  media_type: 'audio' | 'video';
  media_url: string;
  duration_seconds: number;
  thumbnail_url: string;
  genre: string;
  language: string;
  views_count: number;
  playtime_seconds: number;
  is_published: boolean;
  channel?: Channel;
  created_at: string;
}

export interface PayoutRequest {
  id: string;
  channel_id: string;
  channel_name?: string;
  amount: number;
  payout_method: string;
  payout_details: string;
  status: 'pending' | 'processing' | 'paid' | 'rejected';
  requested_at: string;
}

export interface ContentReport {
  id: string;
  reporter_id?: string;
  episode_id: string;
  episode_title?: string;
  channel_name?: string;
  reason: string;
  status: 'pending' | 'reviewed' | 'action_taken' | 'dismissed';
  admin_notes?: string;
  created_at: string;
}

// Initial Seed Data for fallback
const INITIAL_CHANNELS: Channel[] = [
  {
    id: 'ch-1',
    user_id: 'usr-1',
    channel_name: 'Kissa Kahani Studios',
    handle: 'kissakahani',
    bio: 'India\'s premier regional audio storytelling channel. Producing romance, horror & mythological series in Hindi and Marathi.',
    logo_url: '/assets/images/Logo_main.jpeg',
    banner_url: 'https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4',
    is_verified: true,
    monetization_status: 'approved',
    total_subscribers: 540000,
    total_views: 3200000,
    created_at: '2024-01-15T00:00:00Z',
  },
  {
    id: 'ch-2',
    user_id: 'usr-2',
    channel_name: 'Mythic Voices India',
    handle: 'mythicvoices',
    bio: 'Exploring ancient Indian epics, temple legends, and folklore in Tamil, Telugu and Kannada.',
    logo_url: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb',
    banner_url: 'https://images.unsplash.com/photo-1478737270239-2f02b77fc618',
    is_verified: true,
    monetization_status: 'approved',
    total_subscribers: 280000,
    total_views: 1450000,
    created_at: '2024-03-10T00:00:00Z',
  },
  {
    id: 'ch-3',
    user_id: 'usr-3',
    channel_name: 'Midnight Thrillers FM',
    handle: 'midnightthrillers',
    bio: 'Creepy creepypastas, true crime mysteries, and ghost stories narrated by top voice artists.',
    logo_url: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d',
    banner_url: 'https://images.unsplash.com/photo-1514525253161-7a46d19cd819',
    is_verified: false,
    monetization_status: 'pending',
    total_subscribers: 45000,
    total_views: 210000,
    created_at: '2024-06-01T00:00:00Z',
  }
];

const INITIAL_ALBUMS: Album[] = [
  {
    id: 'alb-1',
    channel_id: 'ch-1',
    title: 'Dil Ki Dhadkan Season 1',
    description: 'A passionate college romance story set in Pune.',
    cover_image_url: 'https://img.rocket.new/generatedImages/rocket_gen_img_17f8f5ea4-1763299033127.png',
    genre: 'Romance',
    language: 'Hindi',
    is_published: true,
    total_tracks: 12,
    created_at: '2024-02-01T00:00:00Z',
  },
  {
    id: 'alb-2',
    channel_id: 'ch-2',
    title: 'Mahabharat Unfoldings',
    description: 'Deep dives into lesser-known heroes of Mahabharata.',
    cover_image_url: 'https://img.rocket.new/generatedImages/rocket_gen_img_111e29bbe-1763294966477.png',
    genre: 'Mythology',
    language: 'Tamil',
    is_published: true,
    total_tracks: 8,
    created_at: '2024-03-20T00:00:00Z',
  }
];

const INITIAL_EPISODES: Episode[] = [
  {
    id: 'ep-1',
    channel_id: 'ch-1',
    album_id: 'alb-1',
    title: 'Episode 1: Pehli Mulaqat',
    description: 'Rohan meets Simran at the rain-soaked college canteen.',
    media_type: 'audio',
    media_url: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3',
    duration_seconds: 1845,
    thumbnail_url: 'https://img.rocket.new/generatedImages/rocket_gen_img_17f8f5ea4-1763299033127.png',
    genre: 'Romance',
    language: 'Hindi',
    views_count: 452000,
    playtime_seconds: 780000,
    is_published: true,
    created_at: '2024-02-05T00:00:00Z',
  },
  {
    id: 'ep-2',
    channel_id: 'ch-1',
    album_id: 'alb-1',
    title: 'Episode 2: Unspoken Promises',
    description: 'A sudden misunderstanding threatens to break their bond.',
    media_type: 'audio',
    media_url: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-2.mp3',
    duration_seconds: 2100,
    thumbnail_url: 'https://images.unsplash.com/photo-1518609878373-06d740f60d8b',
    genre: 'Romance',
    language: 'Hindi',
    views_count: 389000,
    playtime_seconds: 640000,
    is_published: true,
    created_at: '2024-02-12T00:00:00Z',
  },
  {
    id: 'ep-3',
    channel_id: 'ch-2',
    album_id: 'alb-2',
    title: 'Karna: The Unsung Hero',
    description: 'Exploring Karna\'s sacrifices and unmatched warrior spirit.',
    media_type: 'video',
    media_url: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4',
    duration_seconds: 1420,
    thumbnail_url: 'https://img.rocket.new/generatedImages/rocket_gen_img_111e29bbe-1763294966477.png',
    genre: 'Mythology',
    language: 'Tamil',
    views_count: 215000,
    playtime_seconds: 410000,
    is_published: true,
    created_at: '2024-03-25T00:00:00Z',
  }
];

const INITIAL_PAYOUTS: PayoutRequest[] = [
  {
    id: 'pay-1',
    channel_id: 'ch-1',
    channel_name: 'Kissa Kahani Studios',
    amount: 45000.00,
    payout_method: 'UPI Direct',
    payout_details: 'kissastudios@okicici',
    status: 'pending',
    requested_at: '2026-08-01T10:00:00Z',
  },
  {
    id: 'pay-2',
    channel_id: 'ch-2',
    channel_name: 'Mythic Voices India',
    amount: 28500.00,
    payout_method: 'HDFC Bank NEFT',
    payout_details: 'A/C 5010023948291, IFSC HDFC0000123',
    status: 'paid',
    requested_at: '2026-07-20T14:30:00Z',
  }
];

const INITIAL_REPORTS: ContentReport[] = [
  {
    id: 'rep-1',
    reporter_id: 'usr-9',
    episode_id: 'ep-2',
    episode_title: 'Episode 2: Unspoken Promises',
    channel_name: 'Kissa Kahani Studios',
    reason: 'Audio quality glitch at minute 12:45.',
    status: 'pending',
    created_at: '2026-08-03T18:20:00Z',
  }
];

class DataService {
  private fallbackChannels: Channel[] = [...INITIAL_CHANNELS];
  private fallbackAlbums: Album[] = [...INITIAL_ALBUMS];
  private fallbackEpisodes: Episode[] = [...INITIAL_EPISODES];
  private fallbackPayouts: PayoutRequest[] = [...INITIAL_PAYOUTS];
  private fallbackReports: ContentReport[] = [...INITIAL_REPORTS];

  // Channels
  async getChannels(): Promise<Channel[]> {
    try {
      const { data, error } = await supabase
        .from('channels')
        .select('*')
        .order('created_at', { ascending: false });

      if (!error && data && data.length > 0) {
        return data as Channel[];
      }
    } catch (err) {
      console.warn('Supabase fetch error, fallback to seed channels:', err);
    }
    return this.fallbackChannels;
  }

  async getChannelByHandle(handle: string): Promise<Channel | undefined> {
    try {
      const { data, error } = await supabase
        .from('channels')
        .select('*')
        .ilike('handle', handle)
        .single();

      if (!error && data) {
        return data as Channel;
      }
    } catch (err) {
      console.warn('Supabase fetch channel by handle info:', err);
    }
    return this.fallbackChannels.find(c => c.handle.toLowerCase() === handle.toLowerCase());
  }

  async createChannel(channelData: Partial<Channel>): Promise<Channel> {
    try {
      const { data, error } = await supabase
        .from('channels')
        .insert([channelData])
        .select()
        .single();

      if (!error && data) {
        return data as Channel;
      }
    } catch (err) {
      console.warn('Supabase createChannel fallback info:', err);
    }

    const newChannel: Channel = {
      id: `ch-${Date.now()}`,
      user_id: channelData.user_id || 'usr-1',
      channel_name: channelData.channel_name || 'My Audio Channel',
      handle: channelData.handle || `channel_${Date.now().toString().slice(-4)}`,
      bio: channelData.bio || 'New audio creator channel.',
      logo_url: channelData.logo_url || '/assets/images/Logo_main.jpeg',
      banner_url: channelData.banner_url || 'https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4',
      is_verified: false,
      monetization_status: 'none',
      total_subscribers: 0,
      total_views: 0,
      created_at: new Date().toISOString(),
    };
    this.fallbackChannels.unshift(newChannel);
    return newChannel;
  }

  async updateChannel(id: string, updates: Partial<Channel>): Promise<Channel> {
    try {
      const { data, error } = await supabase
        .from('channels')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (!error && data) {
        return data as Channel;
      }
    } catch (err) {
      console.warn('Supabase updateChannel fallback info:', err);
    }

    const idx = this.fallbackChannels.findIndex(c => c.id === id);
    if (idx !== -1) {
      this.fallbackChannels[idx] = { ...this.fallbackChannels[idx], ...updates };
      return this.fallbackChannels[idx];
    }
    if (this.fallbackChannels.length > 0) {
      this.fallbackChannels[0] = { ...this.fallbackChannels[0], ...updates };
      return this.fallbackChannels[0];
    }
    throw new Error('Channel not found');
  }

  // Albums
  async getAlbums(channelId?: string): Promise<Album[]> {
    try {
      let query = supabase.from('albums').select('*').order('created_at', { ascending: false });
      if (channelId) {
        query = query.eq('channel_id', channelId);
      }
      const { data, error } = await query;

      if (!error && data && data.length > 0) {
        return data as Album[];
      }
    } catch (err) {
      console.warn('Supabase fetch albums info:', err);
    }

    if (channelId) {
      return this.fallbackAlbums.filter(a => a.channel_id === channelId);
    }
    return this.fallbackAlbums;
  }

  async createAlbum(album: Omit<Album, 'id' | 'created_at' | 'total_tracks'>): Promise<Album> {
    try {
      const { data, error } = await supabase
        .from('albums')
        .insert([{ ...album, total_tracks: 0 }])
        .select()
        .single();

      if (!error && data) {
        return data as Album;
      }
    } catch (err) {
      console.warn('Supabase createAlbum info:', err);
    }

    const newAlbum: Album = {
      ...album,
      id: `alb-${Date.now()}`,
      total_tracks: 0,
      created_at: new Date().toISOString(),
    };
    this.fallbackAlbums.push(newAlbum);
    return newAlbum;
  }

  // Episodes
  async getEpisodes(filter?: { channelId?: string; albumId?: string; publishedOnly?: boolean }): Promise<Episode[]> {
    try {
      let query = supabase.from('episodes').select('*, channel:channels(*)').order('created_at', { ascending: false });
      
      if (filter?.channelId) {
        query = query.eq('channel_id', filter.channelId);
      }
      if (filter?.albumId) {
        query = query.eq('album_id', filter.albumId);
      }
      if (filter?.publishedOnly) {
        query = query.eq('is_published', true);
      }

      const { data, error } = await query;

      if (!error && data && data.length > 0) {
        return data as Episode[];
      }
    } catch (err) {
      console.warn('Supabase getEpisodes info:', err);
    }

    let result = [...this.fallbackEpisodes];
    if (filter?.channelId) {
      result = result.filter(e => e.channel_id === filter.channelId);
    }
    if (filter?.albumId) {
      result = result.filter(e => e.album_id === filter.albumId);
    }
    if (filter?.publishedOnly) {
      result = result.filter(e => e.is_published);
    }

    return result.map(e => ({
      ...e,
      channel: this.fallbackChannels.find(c => c.id === e.channel_id)
    }));
  }

  async getEpisodeById(id: string): Promise<Episode | undefined> {
    try {
      const { data, error } = await supabase
        .from('episodes')
        .select('*, channel:channels(*)')
        .eq('id', id)
        .single();

      if (!error && data) {
        return data as Episode;
      }
    } catch (err) {
      console.warn('Supabase getEpisodeById info:', err);
    }

    const ep = this.fallbackEpisodes.find(e => e.id === id);
    if (!ep) return undefined;
    return {
      ...ep,
      channel: this.fallbackChannels.find(c => c.id === ep.channel_id)
    };
  }

  async createEpisode(episode: Omit<Episode, 'id' | 'created_at' | 'views_count' | 'playtime_seconds'>): Promise<Episode> {
    try {
      const { data, error } = await supabase
        .from('episodes')
        .insert([{
          ...episode,
          views_count: 0,
          playtime_seconds: 0,
        }])
        .select('*, channel:channels(*)')
        .single();

      if (!error && data) {
        return data as Episode;
      }
    } catch (err) {
      console.warn('Supabase createEpisode info:', err);
    }

    const newEp: Episode = {
      ...episode,
      id: `ep-${Date.now()}`,
      views_count: 0,
      playtime_seconds: 0,
      created_at: new Date().toISOString(),
    };
    this.fallbackEpisodes.unshift(newEp);

    const ch = this.fallbackChannels.find(c => c.id === newEp.channel_id);
    if (ch) ch.total_views += 1;

    return newEp;
  }

  async recordPlay(episodeId: string, durationWatched: number) {
    try {
      // Increment Supabase views_count & playtime_seconds
      await supabase.rpc('increment_episode_play', {
        episode_uuid: episodeId,
        watched_sec: Math.round(durationWatched),
      });
    } catch (err) {
      console.warn('Supabase RPC recordPlay info:', err);
    }

    const ep = this.fallbackEpisodes.find(e => e.id === episodeId);
    if (ep) {
      ep.views_count += 1;
      ep.playtime_seconds += durationWatched;
      const ch = this.fallbackChannels.find(c => c.id === ep.channel_id);
      if (ch) ch.total_views += 1;
    }
  }

  // Payouts
  async getPayouts(): Promise<PayoutRequest[]> {
    try {
      const { data, error } = await supabase
        .from('payouts')
        .select('*, channel:channels(channel_name)')
        .order('requested_at', { ascending: false });

      if (!error && data && data.length > 0) {
        return data.map(p => ({
          ...p,
          channel_name: p.channel?.channel_name || 'Creator Channel'
        })) as PayoutRequest[];
      }
    } catch (err) {
      console.warn('Supabase getPayouts info:', err);
    }
    return this.fallbackPayouts;
  }

  async requestPayout(channelId: string, channelName: string, amount: number, method: string, details: string): Promise<PayoutRequest> {
    try {
      const { data, error } = await supabase
        .from('payouts')
        .insert([{
          channel_id: channelId,
          amount,
          payout_method: method,
          payout_details: details,
          status: 'pending',
        }])
        .select()
        .single();

      if (!error && data) {
        return {
          ...data,
          channel_name: channelName
        } as PayoutRequest;
      }
    } catch (err) {
      console.warn('Supabase requestPayout info:', err);
    }

    const req: PayoutRequest = {
      id: `pay-${Date.now()}`,
      channel_id: channelId,
      channel_name: channelName,
      amount,
      payout_method: method,
      payout_details: details,
      status: 'pending',
      requested_at: new Date().toISOString(),
    };
    this.fallbackPayouts.unshift(req);
    return req;
  }

  async updatePayoutStatus(id: string, status: 'pending' | 'processing' | 'paid' | 'rejected'): Promise<PayoutRequest> {
    try {
      const { data, error } = await supabase
        .from('payouts')
        .update({ status })
        .eq('id', id)
        .select()
        .single();

      if (!error && data) {
        return data as PayoutRequest;
      }
    } catch (err) {
      console.warn('Supabase updatePayoutStatus info:', err);
    }

    const p = this.fallbackPayouts.find(item => item.id === id);
    if (p) {
      p.status = status;
      return p;
    }
    throw new Error('Payout request not found');
  }

  // Moderation Reports
  async getReports(): Promise<ContentReport[]> {
    try {
      const { data, error } = await supabase
        .from('content_reports')
        .select('*, episode:episodes(title, channel:channels(channel_name))')
        .order('created_at', { ascending: false });

      if (!error && data && data.length > 0) {
        return data.map(r => ({
          ...r,
          episode_title: r.episode?.title || 'Episode',
          channel_name: r.episode?.channel?.channel_name || 'Channel'
        })) as ContentReport[];
      }
    } catch (err) {
      console.warn('Supabase getReports info:', err);
    }
    return this.fallbackReports;
  }

  async createReport(episodeId: string, reason: string): Promise<ContentReport> {
    try {
      const { data, error } = await supabase
        .from('content_reports')
        .insert([{
          episode_id: episodeId,
          reason,
          status: 'pending',
        }])
        .select()
        .single();

      if (!error && data) {
        return data as ContentReport;
      }
    } catch (err) {
      console.warn('Supabase createReport info:', err);
    }

    const ep = this.fallbackEpisodes.find(e => e.id === episodeId);
    const ch = ep ? this.fallbackChannels.find(c => c.id === ep.channel_id) : undefined;

    const rep: ContentReport = {
      id: `rep-${Date.now()}`,
      episode_id: episodeId,
      episode_title: ep?.title || 'Unknown Episode',
      channel_name: ch?.channel_name || 'Unknown Channel',
      reason,
      status: 'pending',
      created_at: new Date().toISOString(),
    };
    this.fallbackReports.unshift(rep);
    return rep;
  }

  async updateReportStatus(id: string, status: ContentReport['status'], notes?: string): Promise<ContentReport> {
    try {
      const { data, error } = await supabase
        .from('content_reports')
        .update({ status, admin_notes: notes })
        .eq('id', id)
        .select()
        .single();

      if (!error && data) {
        return data as ContentReport;
      }
    } catch (err) {
      console.warn('Supabase updateReportStatus info:', err);
    }

    const rep = this.fallbackReports.find(r => r.id === id);
    if (rep) {
      rep.status = status;
      if (notes) rep.admin_notes = notes;
      return rep;
    }
    throw new Error('Report not found');
  }
}

export const dataService = new DataService();
